import React, { useEffect, useState } from "react";
import Dialog from "@mui/material/Dialog";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import CloseIcon from "@mui/icons-material/Close";
import CircularProgress from "@mui/material/CircularProgress";

import { getBlobAPICall } from "../../../config/apiCalls";

// Full-screen PDF viewer for a HubSpot quote document. Exits only via the close
// button — backdrop clicks and Escape are both ignored, per the intended UX.
export default function DocumentViewerDialog({ open, onClose, document: doc, instance, accounts }) {
	const [blobUrl, setBlobUrl] = useState(null);
	// HubSpot disables a quote's direct PDF download once it expires, even if it was
	// signed beforehand — the server falls back to the hosted quote page in that case,
	// sent back as JSON ({ hostedUrl }) instead of a PDF blob.
	const [hostedUrl, setHostedUrl] = useState(null);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState("");

	useEffect(() => {
		if (!open || !doc?.id) return;
		let cancelled = false;
		setLoading(true);
		setError("");
		setBlobUrl(null);
		setHostedUrl(null);

		getBlobAPICall(instance, accounts[0], "/api/phaseReports/documentPdf", { quoteId: doc.id })
			.then(async (blob) => {
				if (cancelled) return;
				if (blob.type && blob.type.includes("json")) {
					const parsed = JSON.parse(await blob.text());
					if (parsed.hostedUrl) {
						setHostedUrl(parsed.hostedUrl);
						return;
					}
					throw new Error(parsed.message || "Failed to load this document.");
				}
				setBlobUrl(URL.createObjectURL(blob));
			})
			.catch(() => {
				if (!cancelled) setError("Failed to load this document.");
			})
			.finally(() => {
				if (!cancelled) setLoading(false);
			});

		return () => {
			cancelled = true;
		};
	}, [open, doc?.id, instance, accounts]);

	useEffect(() => {
		if (!blobUrl) return;
		return () => URL.revokeObjectURL(blobUrl);
	}, [blobUrl]);

	return (
		<Dialog
			open={open}
			onClose={(_event, reason) => {
				if (reason === "backdropClick" || reason === "escapeKeyDown") return;
				onClose();
			}}
			fullScreen
			disableEscapeKeyDown
		>
			<div style={{ display: "flex", flexDirection: "column", height: "100%", background: "#525659" }}>
				<div
					style={{
						display: "flex",
						alignItems: "center",
						justifyContent: "space-between",
						padding: "10px 20px",
						background: "#323639",
						color: "#fff",
						flexShrink: 0,
					}}
				>
					<div style={{ fontSize: 14, fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
						{doc?.name || "Document"}
					</div>
					<Tooltip title="Close">
						<IconButton onClick={onClose} size="small" style={{ color: "#fff" }}>
							<CloseIcon />
						</IconButton>
					</Tooltip>
				</div>
				<div style={{ flex: 1, overflow: "auto", display: "flex" }}>
					{loading ? (
						<div style={{ margin: "auto", display: "flex", flexDirection: "column", alignItems: "center", gap: 12, color: "#fff" }}>
							<CircularProgress color="inherit" size={32} />
							<div>Loading document...</div>
						</div>
					) : error ? (
						<div style={{ margin: "auto", color: "#fff" }}>{error}</div>
					) : blobUrl || hostedUrl ? (
						<iframe
							src={blobUrl || hostedUrl}
							title={doc?.name || "Document"}
							style={{ border: "none", width: "100%", height: "100%", background: "#fff" }}
						/>
					) : null}
				</div>
			</div>
		</Dialog>
	);
}
